import React, { useEffect, useRef, useState } from 'react';
import Card from '@/components/ui/Card/Card.tsx';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import breakpoints from '@/constants/breakpoints.ts';
import styles from './MissingDataSection.module.scss';
import classNames from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { getDataCompliancePastItineraries } from '@/api/dataCompliance.ts';
import NoDataIcon from '@/assets/Icons/No-Data.svg?react';
import queryKeys from '@/constants/queryKeys.ts';
import { useModalStore } from '@/store/useModalStore.ts';
import TravelerDataComplianceModal from '@/pages/ClimateProDashboard/DataCompliance/MissingDataSection/TravelerDataComplianceModal.tsx';
import EntityRow, {
	MissingTripsButton,
} from '@/pages/ClimateProDashboard/DataCompliance/MissingDataSection/TableRows.tsx';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import Tooltip from '@/components/ui/Tooltip/Tooltip.tsx';
import Co2Period = App.Enums.Co2Period;
import DataComplianceLegalEntityData = App.Data.DataComplianceLegalEntityData;
import DataComplianceUserData = App.Data.DataComplianceUserData;
import { trackEvent } from '@/utils/productAnalyticsUtil.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';

interface MissingDataTableProps {
	timePeriod: Co2Period;
}

type TableFilter = 'legal_entity' | 'user';

const bookerTooltipText = 'The travel arranger is the person who booked the trip.';

const hasSingleBookerPath = (items?: DataComplianceLegalEntityData[] | null) => {
	if (!items) {
		return false;
	}

	const entitiesWithBookers = items.filter(entity =>
		entity.children?.some(office => office.children?.some(department => Boolean(department.children?.length))),
	);
	const officesWithBookers = entitiesWithBookers.flatMap(entity =>
		(entity.children || []).filter(office =>
			office.children?.some(department => Boolean(department.children?.length)),
		),
	);
	const departmentsWithBookers = officesWithBookers.flatMap(office =>
		(office.children || []).filter(department => Boolean(department.children?.length)),
	);
	const bookerIds = new Set(
		departmentsWithBookers.flatMap(department => department.children || []).map(booker => booker.id),
	);
	return (
		entitiesWithBookers.length === 1 &&
		officesWithBookers.length === 1 &&
		departmentsWithBookers.length === 1 &&
		bookerIds.size === 1
	);
};

const MissingDataSection: React.FC<MissingDataTableProps> = ({ timePeriod }) => {
	const { activeModal, openModal } = useModalStore();
	const [selectedTableFilter, setSelectedTableFilter] = useState<TableFilter>('legal_entity');

	const entitiesTableRef = useRef<HTMLButtonElement>(null);
	const bookersTableRef = useRef<HTMLButtonElement>(null);
	const hasCheckedDefaultViewRef = useRef(false);
	const [indicatorLeft, setIndicatorLeft] = useState(0);
	const [indicatorWidth, setIndicatorWidth] = useState(0);

	const openBookerTripsPreview = (userId: number) => {
		openModal('travelerDataCompliance', {
			userId,
		});
		trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.interact, {
			deduplicationKey: `travel-arranger.${userId}.trips`,
			postHogEventName: 'data_gap_trips_clicked',
			postHogProperties: {
				category: 'data-gap',
				source: 'travel_arranger_table',
			},
		});
	};
	const handleTableFilterChange = (filter: TableFilter) => {
		if (filter === selectedTableFilter) {
			return;
		}

		setSelectedTableFilter(filter);
		trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.interact, {
			deduplicationKey: `group-by.${filter}`,
		});
	};

	const dataComplianceItinerariesQuery = useQuery({
		queryKey: [queryKeys.dataCompliancePastTrips, timePeriod, selectedTableFilter],
		queryFn: async () => {
			const params = {
				period: timePeriod,
				groupBy: selectedTableFilter,
			};
			return await getDataCompliancePastItineraries(params);
		},
		enabled: !!timePeriod,
		retry: false,
	});

	const items = dataComplianceItinerariesQuery.data?.items || [];
	const entityItems = selectedTableFilter === 'legal_entity' ? (items as DataComplianceLegalEntityData[]) : undefined;
	const userItems = selectedTableFilter === 'user' ? (items as DataComplianceUserData[]) : undefined;

	useEffect(() => {
		hasCheckedDefaultViewRef.current = false;
		setSelectedTableFilter('legal_entity');
	}, [timePeriod]);

	useEffect(() => {
		if (
			hasCheckedDefaultViewRef.current ||
			selectedTableFilter !== 'legal_entity' ||
			!dataComplianceItinerariesQuery.isSuccess
		) {
			return;
		}

		hasCheckedDefaultViewRef.current = true;

		if (hasSingleBookerPath(dataComplianceItinerariesQuery.data?.items as DataComplianceLegalEntityData[] | null)) {
			setSelectedTableFilter('user');
		}
	}, [dataComplianceItinerariesQuery.data, dataComplianceItinerariesQuery.isSuccess, selectedTableFilter]);

	useEffect(() => {
		const activeButton =
			selectedTableFilter === 'legal_entity' ? entitiesTableRef.current : bookersTableRef.current;

		if (activeButton) {
			const rect = activeButton.getBoundingClientRect();
			const parentRect = activeButton.parentElement?.getBoundingClientRect();

			if (parentRect) {
				setIndicatorLeft(rect.left - parentRect.left);
				setIndicatorWidth(rect.width);
			}
		}
	}, [selectedTableFilter]);

	return (
		<>
			<Card className={styles.missingDataCard}>
				<FlexBlock
					justifyContent="space-between"
					flexDirection={{ [breakpoints.zero]: 'column', [breakpoints.tabletWide]: 'row' }}
					rowGap="14px"
					className={styles.cardHeader}
				>
					<FlexBlock flexDirection="column" rowGap="16px">
						<h3>Where trip details are missing</h3>
						<p>
							See which trips are missing accommodation or transport details, grouped by organization or
							travel arranger.
						</p>
					</FlexBlock>
				</FlexBlock>
				<FlexBlock className={styles.selectedTableFilterBttnsWrapper}>
					<button
						ref={entitiesTableRef}
						className={classNames(selectedTableFilter === 'legal_entity' && styles.active)}
						onClick={() => handleTableFilterChange('legal_entity')}
					>
						By organization
					</button>
					<button
						ref={bookersTableRef}
						className={classNames(selectedTableFilter === 'user' && styles.active)}
						onClick={() => handleTableFilterChange('user')}
					>
						By travel arranger
					</button>
					<span
						className={styles.activeIndicator}
						style={{
							transform: `translateX(${indicatorLeft}px)`,
							width: `${indicatorWidth}px`,
						}}
					/>
				</FlexBlock>

				{selectedTableFilter === 'legal_entity' && (
					<div className={styles.tableWrapper}>
						<table className={styles.entityTable}>
							<colgroup>
								<col className={styles.entityNameColumn} />
								<col className={styles.countColumn} />
								<col className={styles.countColumn} />
								<col className={styles.countColumn} />
								<col className={styles.missingColumn} />
							</colgroup>
							<thead>
								<tr>
									<th colSpan={3}>Legal entity</th>
									<th className={styles.alignRight}>
										<FlexBlock
											alignItems="center"
											justifyContent="flex-end"
											columnGap="2px"
											className={styles.filter}
										>
											Travel arrangers{' '}
											<div className={styles.tooltip}>
												<Tooltip text={bookerTooltipText} />
											</div>
										</FlexBlock>
									</th>
									<th className={styles.alignRight}>Missing details</th>
								</tr>
							</thead>
							<tbody>{entityItems?.map(entity => <EntityRow key={entity.id} entity={entity} />)}</tbody>
						</table>
					</div>
				)}

				{selectedTableFilter === 'user' && (
					<div className={styles.tableWrapper}>
						<table className={styles.bookerTable}>
							<colgroup>
								<col className={styles.bookerNameColumn} />
								<col className={styles.bookerEntityColumn} />
								<col className={styles.bookerMetaColumn} />
								<col className={styles.bookerMetaColumn} />
								<col className={styles.bookerTripsColumn} />
								<col className={styles.missingColumn} />
							</colgroup>
							<thead>
								<tr>
									<th>
										<FlexBlock alignItems="center" columnGap="2px" className={styles.filter}>
											Travel arranger{' '}
											<div className={styles.tooltip}>
												<Tooltip text={bookerTooltipText} />
											</div>
										</FlexBlock>
									</th>
									<th>Legal entity</th>
									<th>Department</th>
									<th>Office</th>
									<th className={styles.alignRight}>Total trips</th>
									<th className={styles.alignRight}>Missing details</th>
								</tr>
							</thead>
							<tbody>
								{userItems?.map(item => (
									<tr
										key={item.id}
										className={styles.clickableRow}
										onClick={() => openBookerTripsPreview(item.id)}
									>
										<td>
											<FlexBlock columnGap="4px" className={styles.userName}>
												{item.name}
											</FlexBlock>
										</td>
										<td className={styles.entity}>
											<span>{item.legalEntityName || '–'}</span>
										</td>
										<td>{item.departmentName || '–'}</td>
										<td>{item.officeName || '–'}</td>
										<td className={classNames(styles.totalTrips, styles.alignRight)}>
											<span>{item.itineraries}</span>
										</td>
										<td className={classNames(styles.missingLogs, styles.alignRight)}>
											<MissingTripsButton
												count={item.nonCompliantItinerariesCount}
												onClick={() => openBookerTripsPreview(item.id)}
											/>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}

				{dataComplianceItinerariesQuery.isLoading && (
					<div className={styles.spinnerWrapper}>
						<Spinner absolute />
					</div>
				)}

				{!items.length && dataComplianceItinerariesQuery.isSuccess && (
					<FlexBlock flexDirection="column" rowGap="7px" alignItems="center" className={styles.noDataOverlay}>
						<NoDataIcon />
						<p>Trip details complete for this period</p>
						<p>All required trip details have been provided for completed trips in this period.</p>
					</FlexBlock>
				)}

				{dataComplianceItinerariesQuery.isError && (
					<FlexBlock flexDirection="column" rowGap="7px" alignItems="center" className={styles.errorOverlay}>
						<p>We couldn’t load the trip data. Try again in a moment.</p>
					</FlexBlock>
				)}
			</Card>
			{activeModal === 'travelerDataCompliance' && <TravelerDataComplianceModal timePeriod={timePeriod} />}
		</>
	);
};

export default MissingDataSection;
